import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import OrbLoader from "../common/OrbLoader";
import dayjs from "dayjs";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import IOMarginDialog from "../dialogs/IOMarginDialog";
import {
  useDeleteIOMarginPeriodMutation,
  useUpdateIOMarginPeriodMutation,
  useAddIOMarginPeriodMutation,
  insertionOrderMarginApi,
} from "../../store/apis/insertionOrderMarginApi";
import { formatPercentValue } from "../../utils/numberFormat";

// Define the types for raw margin periods and inputs
// IO raw margin period from DB
type InsertionOrderMarginPeriod =
  components["schemas"]["InsertionOrderMarginPeriod"];
type CreateIOMarginPeriodInput =
  components["schemas"]["CreateIOMarginPeriodInput"];

interface IODbMarginTableProps {
  rawMargins: InsertionOrderMarginPeriod[];
  isLoading: boolean;
  insertionOrderId: string;
  startDate?: string | null;
  endDate?: string | null;
}

const IODbMarginTable: React.FC<IODbMarginTableProps> = ({
  rawMargins,
  isLoading,
  insertionOrderId,
  startDate,
  endDate,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [
    deleteIOMarginPeriod,
    { isLoading: isDeleting, isSuccess: isDeletingSuccess, error: deleteError },
  ] = useDeleteIOMarginPeriodMutation();
  const [
    updateIOMarginPeriod,
    { isLoading: isUpdating, isSuccess: isUpdatingSuccess, error: updateError },
  ] = useUpdateIOMarginPeriodMutation();
  const [
    addIOMarginPeriod,
    { isLoading: isAdding, isSuccess: isAddingSuccess, error: addError },
  ] = useAddIOMarginPeriodMutation();

  // State for edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentMargin, setCurrentMargin] =
    useState<InsertionOrderMarginPeriod | null>(null);

  // State for add dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [marginToDelete, setMarginToDelete] = useState<string | null>(null);

  const handleEditClick = (margin: InsertionOrderMarginPeriod) => {
    setCurrentMargin(margin);
    setEditDialogOpen(true);
  };

  const handleAddClick = () => {
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (marginId: string) => {
    setMarginToDelete(marginId);
    setDeleteDialogOpen(true);
  };

  const handleEditClose = useCallback(() => {
    setEditDialogOpen(false);
    setCurrentMargin(null);
  }, []);

  const handleAddClose = useCallback(() => {
    setAddDialogOpen(false);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteDialogOpen(false);
    setMarginToDelete(null);
  }, []);

  // Effects for update/add/delete
  React.useEffect(() => {
    if (isUpdatingSuccess) {
      dispatch(
        insertionOrderMarginApi.util.invalidateTags(["InsertionOrderMargins"])
      );
      dispatch(
        setToastMessage({
          message: t(
            "metrics.marginUpdatedSuccess",
            "Margin period updated successfully"
          ),
          severity: "success",
        })
      );
      handleEditClose();
    }
    if (updateError) {
      dispatch(
        setToastMessage({
          message: t(
            "metrics.marginUpdateError",
            "Failed to update margin period: {{error}}",
            {
              error:
                updateError instanceof Error
                  ? updateError.message
                  : "Unknown error",
            }
          ),
          severity: "error",
        })
      );
    }
  }, [isUpdatingSuccess, updateError, dispatch, t, handleEditClose]);

  const handleSaveEdit = (
    marginData: CreateIOMarginPeriodInput
  ): Promise<void> => {
    if (!currentMargin) return Promise.resolve();
    return updateIOMarginPeriod({ id: currentMargin.id, data: marginData })
      .unwrap()
      .then(() => {})
      .catch((err) => {
        console.error("Failed to update IO margin period:", err);
      });
  };

  React.useEffect(() => {
    if (isAddingSuccess) {
      dispatch(
        insertionOrderMarginApi.util.invalidateTags(["InsertionOrderMargins"])
      );
      dispatch(
        setToastMessage({
          message: t(
            "metrics.marginAddedSuccess",
            "Margin period added successfully"
          ),
          severity: "success",
        })
      );
      handleAddClose();
    }
    if (addError) {
      dispatch(
        setToastMessage({
          message: t(
            "metrics.marginAddError",
            "Failed to add margin period: {{error}}",
            {
              error:
                addError instanceof Error ? addError.message : "Unknown error",
            }
          ),
          severity: "error",
        })
      );
    }
  }, [isAddingSuccess, addError, dispatch, t, handleAddClose]);

  const handleSaveAdd = (
    marginData: CreateIOMarginPeriodInput
  ): Promise<void> => {
    return addIOMarginPeriod({ id: insertionOrderId, data: marginData })
      .unwrap()
      .then(() => {})
      .catch((err) => {
        console.error("Failed to add IO margin period:", err);
      });
  };

  React.useEffect(() => {
    if (isDeletingSuccess) {
      dispatch(
        insertionOrderMarginApi.util.invalidateTags(["InsertionOrderMargins"])
      );
      dispatch(
        setToastMessage({
          message: t(
            "metrics.marginDeletedSuccess",
            "Margin period deleted successfully"
          ),
          severity: "success",
        })
      );
      handleDeleteClose();
    }
    if (deleteError) {
      dispatch(
        setToastMessage({
          message: t(
            "metrics.marginDeleteError",
            "Failed to delete margin period: {{error}}",
            {
              error:
                deleteError instanceof Error
                  ? deleteError.message
                  : "Unknown error",
            }
          ),
          severity: "error",
        })
      );
    }
  }, [isDeletingSuccess, deleteError, dispatch, t, handleDeleteClose]);

  const handleConfirmDelete = (): Promise<void> => {
    if (!marginToDelete) return Promise.resolve();

    return deleteIOMarginPeriod(marginToDelete)
      .unwrap()
      .then(() => {})
      .catch((err) => {
        console.error("Failed to delete IO margin period:", err);
      });
  };

  const AddButton = () => (
    <Tooltip title={t("metrics.addMargin", "Add Margin Period") as string}>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddClick}
      >
        {t("common.add", "Add")}
      </Button>
    </Tooltip>
  );

  if (isLoading) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 4,
            mb: 2,
          }}
        >
          <Typography variant="h5">
            {t("metrics.rawMarginData", "Raw Margin Data")}
          </Typography>
          <AddButton />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <OrbLoader
            size="large"
            label={t("common.loading", { defaultValue: "Cargando…" })}
          />
        </Box>
      </>
    );
  }

  if (!rawMargins || rawMargins.length === 0) {
    return (
      <>
        <IOMarginDialog
          open={addDialogOpen}
          onClose={handleAddClose}
          onSave={handleSaveAdd}
          isLoading={isAdding}
          title={t("metrics.addMargin", "Add Margin Period") as string}
          campaignStartDate={startDate || undefined}
          campaignEndDate={endDate || undefined}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 4,
            mb: 2,
          }}
        >
          <Typography variant="h5">
            {t("metrics.rawMarginData", "Raw Margin Data")}
          </Typography>
          <AddButton />
        </Box>
        <Box sx={{ mt: 2, p: 3, textAlign: "center" }}>
          <Typography variant="body1">
            {t(
              "metrics.noMarginData",
              "No margin data available for this insertion order."
            )}
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 4,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("metrics.rawMarginData", "Raw Margin Data")}
        </Typography>
        <AddButton />
      </Box>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="raw IO margins table">
          <TableHead>
            <TableRow
              sx={{ backgroundColor: theme.palette.background.default }}
            >
              <TableCell>{t("common.id", "ID")}</TableCell>
              <TableCell>
                {t("metrics.marginTable.startDate", "Start Date")}
              </TableCell>
              <TableCell>
                {t("metrics.marginTable.endDate", "End Date")}
              </TableCell>
              <TableCell align="right">
                {t("metrics.marginTable.margin", "Margin")} (%)
              </TableCell>
              <TableCell>{t("common.createdAt", "Created At")}</TableCell>
              <TableCell>{t("common.updatedAt", "Updated At")}</TableCell>
              <TableCell align="center">
                {t("common.actions", "Actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rawMargins.map((margin) => (
              <TableRow
                key={margin.id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                }}
              >
                <TableCell component="th" scope="row">
                  <Typography variant="body2">{margin.id}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {dayjs(margin.startDate).format("MMM D, YYYY")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {dayjs(margin.endDate).format("MMM D, YYYY")}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {formatPercentValue(margin.margin)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {dayjs(margin.createdAt).format("MMM D, YYYY HH:mm")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {dayjs(margin.updatedAt).format("MMM D, YYYY HH:mm")}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(margin)}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(margin.id)}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <IOMarginDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        onSave={handleSaveEdit}
        margin={currentMargin || undefined}
        isLoading={isUpdating}
        title={t("metrics.editMargin", "Edit Margin Period") as string}
        campaignStartDate={startDate || undefined}
        campaignEndDate={endDate || undefined}
      />

      {/* Add Dialog */}
      <IOMarginDialog
        open={addDialogOpen}
        onClose={handleAddClose}
        onSave={handleSaveAdd}
        isLoading={isAdding}
        title={t("metrics.addMargin", "Add Margin Period") as string}
        campaignStartDate={startDate || undefined}
        campaignEndDate={endDate || undefined}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>
          {t("metrics.deleteMargin", "Delete Margin Period")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              "metrics.deleteMarginConfirmation",
              "Are you sure you want to delete this margin period? This action cannot be undone."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <CircularProgress size={24} />
            ) : (
              t("common.delete", "Delete")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IODbMarginTable;
